import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'tmm_camp_votes';
const VOTED_KEY = 'tmm_camp_voted';

export interface VoteMap {
  [campId: number]: number;
}

export interface VotedMap {
  [campId: number]: boolean;
}

function loadVotes(): VoteMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function loadVoted(): VotedMap {
  try {
    const raw = localStorage.getItem(VOTED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// Seed initial votes so the feature looks active from day one
const SEED_VOTES: VoteMap = {
  62: 47, 2: 38, 12: 35, 6: 33, 4: 29,
  7: 27, 10: 24, 9: 22, 5: 21, 11: 19,
  36: 18, 27: 16, 33: 15, 28: 14, 53: 13,
};

export function useVotes() {
  const [votes, setVotes] = useState<VoteMap>(() => {
    const stored = loadVotes();
    // Merge seed votes with stored (stored takes precedence for camps already voted on)
    const merged: VoteMap = { ...SEED_VOTES };
    Object.keys(stored).forEach((key) => {
      const id = Number(key);
      merged[id] = stored[id];
    });
    return merged;
  });

  const [voted, setVoted] = useState<VotedMap>(() => loadVoted());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(votes));
  }, [votes]);

  useEffect(() => {
    localStorage.setItem(VOTED_KEY, JSON.stringify(voted));
  }, [voted]);

  const vote = useCallback((campId: number) => {
    if (voted[campId]) return;
    setVotes((prev) => ({ ...prev, [campId]: (prev[campId] ?? 0) + 1 }));
    setVoted((prev) => ({ ...prev, [campId]: true }));
  }, [voted]);

  const getVotes = useCallback((campId: number) => votes[campId] ?? 0, [votes]);
  const hasVoted = useCallback((campId: number) => !!voted[campId], [voted]);

  const topCamps = Object.entries(votes)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([id, count]) => ({ campId: Number(id), count }));

  return { vote, getVotes, hasVoted, topCamps };
}
